/**
 * Shared schema.org JSON-LD builders for public pages.
 *
 * Site-wide Organization and SoftwareApplication schemas live statically in
 * index.html; everything page-scoped is built here and rendered through
 * <PageMeta jsonLd={...}>.
 */

import { SITE_URL, BLOG_OG_IMAGE, canonicalUrl } from "@/lib/seo";

export interface FaqEntry {
  question: string;
  /** Plain-text answer (no JSX) — this is what crawlers read. */
  answer: string;
}

export const faqPageJsonLd = (entries: ReadonlyArray<FaqEntry>): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: entries.map(entry => ({
    "@type": "Question",
    name: entry.question,
    acceptedAnswer: { "@type": "Answer", text: entry.answer },
  })),
});

export interface BlogPostingInput {
  title: string;
  description: string;
  path: string;
  /** ISO dates (YYYY-MM-DD). */
  datePublished: string;
  dateModified?: string;
  authorName: string;
  image?: string;
  /** Category display name, emitted as articleSection. */
  section?: string;
}

export const blogPostingJsonLd = (post: BlogPostingInput): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.description,
  datePublished: post.datePublished,
  dateModified: post.dateModified ?? post.datePublished,
  image: post.image ?? BLOG_OG_IMAGE,
  ...(post.section ? { articleSection: post.section } : {}),
  author: { "@type": "Person", name: post.authorName },
  publisher: {
    "@type": "Organization",
    name: "Stasht",
    url: SITE_URL,
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl(post.path) },
});

export interface BreadcrumbInput {
  name: string;
  path: string;
}

export const breadcrumbJsonLd = (crumbs: ReadonlyArray<BreadcrumbInput>): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: canonicalUrl(crumb.path),
  })),
});

export interface ItemListEntry {
  name: string;
  url: string;
}

export const itemListJsonLd = (name: string, items: ReadonlyArray<ItemListEntry>): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    url: item.url,
  })),
});
